import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import type { SimpleChange } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  output,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';

import { isCandidateDataVisible } from '@core/candidate-util';
import { TranslatePipe } from '@core/i18n-legacy';
import { AppNavigatorService } from '@core/navigation-legacy';
import type { CandidateSelectOption } from '@core/search-types';
import {
  AvatarComponent,
  ButtonComponent,
  ConnectedOverlayDirective,
  DropdownComponent,
  DropdownItemComponent,
  HeadingComponent,
  OverlayOriginDirective,
  RtStatefulComponent,
  RtTooltip,
  TruncateComponent,
} from '@core/user-interface-legacy';

type State = {
  dropdownOpen: boolean;
};

@Component({
  selector: 'rt-candidates-multi-select-item',
  styleUrls: ['./candidates-multi-select-item.component.less'],
  templateUrl: './candidates-multi-select-item.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    OverlayOriginDirective,
    ButtonComponent,
    AvatarComponent,
    TruncateComponent,
    RtTooltip,
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownItemComponent,
    HeadingComponent,
    TranslatePipe,
  ],
})
export class CandidatesMultiSelectItemComponent extends RtStatefulComponent<State> {
  private readonly appNavigator = inject(AppNavigatorService);
  protected readonly cdRef: ChangeDetectorRef;

  public readonly candidate = input<CandidateSelectOption>(undefined!);
  public readonly editable = input<boolean>(false);
  public readonly deletable = input<boolean>(false);
  public readonly button = input<boolean>(false);
  public readonly actOnClick = input<boolean>(true);
  public itemClick = output<void>();
  public editClick = output<void>();
  public removeClick = output<void>();

  public dropdownConfig: ConnectionPositionPair[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
  ];
  public isCandidateDataVisible = true;

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      dropdownOpen: false,
    });
  }

  public ngOnChanges(changes: SimpleChange): void {
    if ('candidate' in changes) {
      this.isCandidateDataVisible = isCandidateDataVisible(this.candidate());
    }
  }

  public get isntAssigned(): boolean {
    const candidate = this.candidate();
    return (<any>this.candidate()).placements
      ? (<any>this.candidate()).placements.length === 0
      : candidate.offers
        ? candidate.offers.length === 0
        : false;
  }

  public handleItemClick(): void {
    if (this.actOnClick()) {
      if (this.deletable() || this.editable()) {
        this.toggleDropdown(true);
      } else {
        this.showCandidate();
      }
    } else {
      this.itemClick.emit();
    }
  }

  public toggleDropdown(dropdownOpen: boolean): void {
    this.setState({ dropdownOpen });
  }

  public showCandidate(): void {
    this.appNavigator.openCandidateDialog(this.candidate().id);
  }
}
